// src/components/SubmitTime/fields/NumericIntegerField.tsx
// Modern React implementation of NumericIntegerField custom field

import React, { useState, useEffect } from 'react';
import type { CustomFieldProps } from '../types';
import { authStorage, sessionStorageUtil } from '@lib/storage';
import './NumericIntegerField.css';

interface NumericIntegerFieldProps extends CustomFieldProps {}

export const NumericIntegerField: React.FC<NumericIntegerFieldProps> = ({
  customField,
  canEdit,
  conversation,
  handleDynamicInputChange,
  UpdateCustomFields,
  showRequiredError,
}) =>
{
  const [value, setValue] = useState<string | number>(
    customField.Value === null || customField.Value === undefined ? '' : customField.Value,
  );

  const personId = authStorage.getPersonId();
  const personData = sessionStorageUtil.getPersonData();

  const isAdmin = personData?.isAdmin ?? false;
  const canEditOwner =
    conversation && personId ? conversation.OwnedById === parseInt(personId, 10) : false;

  // Update value when customField prop changes
  useEffect(() =>
  {
    const newValue =
      customField.Value === null || customField.Value === undefined ? '' : customField.Value;
    setValue(newValue);
  }, [customField.Value]);

  // Validate if user can edit "Actual" field
  const validateOwners = (): boolean =>
  {
    if (customField.Name !== 'Actual')
    {
      return true;
    }
    return canEditOwner || isAdmin;
  };

  const isInteger = (x: string): string =>
  {
    const n = x.toString().replace(/(?!-)[^0-9]/g, '');
    if (x.includes('-'))
    {
      if (x === '-')
      {
        return x;
      }
      else
      {
        const o = Math.sign(Number(n));
        return o === -1 || o === 1 ? n : '';
      }
    }
    else
    {
      return n;
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
  {
    const inputValue = event.target.value;
    const parsedValue = isInteger(inputValue);
    setValue(parsedValue);
    handleDynamicInputChange(
      customField.CustomFieldId,
      parsedValue === '' ? null : Number(parsedValue),
    );
  };

  const handleBlur = () =>
  {
    if (UpdateCustomFields)
    {
      UpdateCustomFields(customField);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) =>
  {
    // Allow: backspace, delete, tab, escape, enter, arrows, minus sign
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      '-',
    ];

    if (
      allowedKeys.includes(event.key) ||
      (event.key >= '0' && event.key <= '9') ||
      (event.ctrlKey &&
        (event.key === 'a' || event.key === 'c' || event.key === 'v' || event.key === 'x'))
    )
    {
      return;
    }

    event.preventDefault();
  };

  const isFieldDisabled = !canEdit || !customField.ConversationIsEnable || !validateOwners();

  return (
    <input
      type="text"
      inputMode="numeric"
      className={`corgee-inpuSubmitTimet right numeric-integer-field${showRequiredError ? ' submit-required-error' : ''}`}
      disabled={isFieldDisabled}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={customField.Placeholder}
      autoComplete="off"
    />
  );
};

export default NumericIntegerField;
